/**
 * Lab mapping-smart-member.html — add/remove Smart Members for Bet Ticker.
 */
import { Button, Modal, Spinner, useToast } from '@w7/ui'
import { useState } from 'react'
import { CheaterPlayerPicker } from '@/features/security/CheaterPlayerPicker'
import {
  useBettickerMapping,
  useUpdateSmartUser,
} from '@/api/hooks/bet-ticker/useSmartMembers'

export type SmartMembersModalProps = {
  open: boolean
  onClose: () => void
  /** Access Level — false = open modal read-only (no add/remove). */
  canEdit?: boolean
}

/** Add spinner on Add btn; remove spinner on that chip’s ×. */
type SmartBusy = { op: 'add' } | { op: 'remove'; item: string } | null

export function SmartMembersModal({ open, onClose, canEdit = true }: SmartMembersModalProps) {
  const toast = useToast()
  const [player, setPlayer] = useState<string | null>(null)
  const [busy, setBusy] = useState<SmartBusy>(null)
  const mappingQ = useBettickerMapping(open)
  const updateM = useUpdateSmartUser()
  const pending = updateM.isPending
  const locked = !canEdit || pending

  const mapped = mappingQ.data?.mappedSmartUsers ?? []

  const add = () => {
    if (!canEdit) return
    const name = player?.trim()
    if (!name || pending) {
      if (!name) toast.error('Please select a valid player.')
      return
    }
    if (mapped.some((u) => u.toLowerCase() === name.toLowerCase())) {
      toast.error('Member is already added')
      return
    }
    setBusy({ op: 'add' })
    updateM.mutate(
      { playerName: name, smartUser: true },
      {
        onSuccess: (res) => {
          toast.success(res.message || 'Member added successfully')
          setPlayer(null)
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : String(err)),
        onSettled: () => setBusy(null),
      },
    )
  }

  const remove = (name: string) => {
    if (!canEdit || pending) return
    setBusy({ op: 'remove', item: name })
    updateM.mutate(
      { playerName: name, smartUser: false },
      {
        onSuccess: (res) => toast.success(res.message || 'Member removed successfully'),
        onError: (err) => toast.error(err instanceof Error ? err.message : String(err)),
        onSettled: () => setBusy(null),
      },
    )
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (pending) return
        setPlayer(null)
        onClose()
      }}
      title="Smart Member Mapping"
      closeDisabled={pending}
    >
      <div className="w7-bm-smart-modal">
        <p className="w7-bm-smart-modal-hint">
          Mapped members show a green flag on Bet Ticker. This does not filter the bet list.
        </p>
        {!canEdit ? (
          <p className="w7-bm-smart-modal-hint" data-testid="smart-members-readonly">
            View only — Smart Members Access Level is off for this page.
          </p>
        ) : null}
        <div className="w7-bm-smart-modal-add">
          <CheaterPlayerPicker
            value={player}
            onChange={canEdit ? setPlayer : () => undefined}
          />
          <Button
            type="button"
            variant="primary"
            busy={busy?.op === 'add'}
            disabled={!canEdit || !player?.trim() || pending}
            onClick={add}
          >
            Add
          </Button>
        </div>
        {mapped.length > 0 ? (
          <div className="w7-bm-smart-modal-selected">
            <span className="w7-bm-smart-modal-label">Selected:</span>
            <ul className="w7-bm-smart-chips">
              {mapped.map((name) => {
                const removing = busy?.op === 'remove' && busy.item === name
                return (
                  <li key={name} className="w7-bm-smart-chip">
                    <span>{name}</span>
                    <button
                      type="button"
                      className="w7-bm-smart-chip-x"
                      aria-label={removing ? `Removing ${name}` : `Remove ${name}`}
                      aria-busy={removing}
                      disabled={locked}
                      onClick={() => remove(name)}
                    >
                      {removing ? <Spinner size={12} /> : '×'}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ) : mappingQ.isLoading ? (
          <p className="w7-bm-smart-modal-empty">Loading…</p>
        ) : (
          <p className="w7-bm-smart-modal-empty">No Smart Members mapped yet.</p>
        )}
      </div>
    </Modal>
  )
}
